import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight,
  Beaker,
  FileCheck,
  Users,
  Database,
  CheckCircle,
  Building2,
  Cog,
} from "lucide-react";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";
import MidPageCTA from "../components/MidPageCTA";
import AnimateOnScroll from "../components/AnimateOnScroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LifeSciences = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const params = new URLSearchParams({
        ...formData,
        service_interest: "life-sciences",
      });
      await fetch(`${API}/contact?${params.toString()}`, { method: "POST" });
      toast.success("Thank you! We'll be in touch soon.");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const services = [
    {
      icon: Cog,
      title: "Process Consulting for Pharma & Biotech",
      description: "We help your team build processes that are efficient, compliant, and scalable.",
      features: [
        "SOP Development: Clear, actionable guides your team can actually follow",
        "Workflow Analysis & Improvement: Identify bottlenecks and optimize day-to-day operations",
        "COA (Certificate of Analysis) Review & Documentation: Ensure quality, accuracy, and compliance",
        "Regulatory Readiness: Keep your operations inspection-ready and compliant",
        "Scalable Process Design: Build workflows that grow with your team",
      ],
    },
    {
      icon: Users,
      title: "Talent Acquisition & HR Support",
      description: "Hiring and onboarding doesn't have to slow you down. We help by:",
      features: [
        "Hiring Process Optimization: Streamline job postings, candidate screening, and interviews",
        "Candidate Shortlisting & Selection: Present your top candidates efficiently",
        "Onboarding & Training Programs: Get new hires contributing quickly",
        "Employee Records & Compliance Management: Keep HR documentation organized and audit-ready",
      ],
    },
    {
      icon: Database,
      title: "Data Management & Documentation Support",
      description: "Small teams often need help keeping data organized and accessible.",
      features: [
        "Lab & Study Data Tracking: Organize experimental results, quality metrics, and batch records",
        "Regulatory Document Preparation: Assist with audit-ready documents and submissions",
        "Reporting & Metrics Dashboards: Build simple dashboards to monitor KPIs, compliance, and productivity",
        "Document Control & Versioning: Keep SOPs, COAs, and protocols up-to-date and easily accessible",
      ],
    },
  ];

  const whyTrustUs = [
    {
      title: "Expertise You Can Count On",
      description: "We bring hands-on experience in pharma and biotech operations, compliance, HR, and data management, helping small teams run efficiently and stay compliant.",
    },
    {
      title: "Tailored, Practical Solutions",
      description: "Our services are designed for startups and growing teams, saving time, reducing errors, and improving workflows without unnecessary complexity.",
    },
    {
      title: "Collaborative, Growth-Focused Approach",
      description: "We work closely with your team to understand your goals, address your challenges, and implement solutions that build a strong foundation for sustainable growth.",
    },
  ];

  return (
    <div>
      <SEO 
        title="Life Sciences Consulting"
        description="Operational, HR, and compliance support for biotech, pharma, and life sciences startups. We help growing teams build the infrastructure to move fast without breaking things."
        ogImage="https://customer-assets.emergentagent.com/job_e6aa7c2c-d03d-4974-9cb2-630ed0a0b376/artifacts/t82rrjuh_Life-Sciences.jpeg"
        keywords="life sciences consulting, biotech startup operations, pharma startup help, life sciences HR support, biotech compliance consulting, FDA compliance startup, laboratory operations consulting"
      />
      <StructuredData data={[{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.rnbconsultinggroup.com" },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://www.rnbconsultinggroup.com/services" },
          { "@type": "ListItem", "position": 3, "name": "Life Sciences", "item": "https://www.rnbconsultinggroup.com/life-sciences" }
        ]
      }]} />
      
      {/* Hero Section */}
      <section className="relative py-28 bg-gradient-to-br from-rnb-navy to-rnb-navy/90 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-rnb-teal/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-rnb-blue/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimateOnScroll direction="left">
              <div>
                <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full mb-6">
                  <Beaker className="w-4 h-4 text-rnb-teal mr-2" />
                  <span className="text-sm font-medium text-white">Pharma & Biotech Operations Support</span>
                </div>

                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                  Smart Solutions for Growing Startups and Small Teams
                </h1>

                <p className="text-lg text-slate-300 mb-8">
                  Running a biotech or pharma startup is exciting but managing operations, compliance, 
                  and talent at the same time can be overwhelming. RNB Consulting Group is here to help. 
                  We streamline processes, ensure quality, and set your team up for growth so you can 
                  focus on innovation.
                </p>

                <Link to="/contact">
                  <Button size="lg" className="bg-rnb-teal hover:bg-rnb-teal/90 btn-hover-lift" data-testid="life-sciences-cta">
                    Schedule Free Consultation
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </AnimateOnScroll>
            <AnimateOnScroll direction="right">
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_e6aa7c2c-d03d-4974-9cb2-630ed0a0b376/artifacts/t82rrjuh_Life-Sciences.jpeg"
                  alt="Lab worker in protective equipment conducting research with microscope and test tubes"
                  className="img-warm w-full rounded-2xl shadow-xl h-56 sm:h-64 lg:h-auto object-cover"
                  data-testid="life-sciences-hero-image"
                />
              </div>
            </AnimateOnScroll>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Our Pharma & Biotech Services
            </h2>
          </div>

          <div className="space-y-12">
            {services.map((service, index) => (
              <Card key={service.title || service.name || service.label || index} className="border-0 shadow-lg overflow-hidden">
                <CardContent className="p-0">
                  <div className="grid lg:grid-cols-3 gap-0">
                    <div className="bg-rnb-light p-8 flex flex-col justify-center">
                      <div className="w-16 h-16 bg-rnb-blue/10 rounded-2xl flex items-center justify-center mb-6">
                        <service.icon className="w-8 h-8 text-rnb-blue" />
                      </div>
                      <h3 className="font-heading text-2xl font-bold text-rnb-navy mb-3">
                        {service.title}
                      </h3>
                      <p className="text-rnb-gray">{service.description}</p>
                    </div>
                    <div className="lg:col-span-2 p-8">
                      <ul className="space-y-4">
                        {service.features.map((feature, fIndex) => (
                          <li key={fIndex} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0 mt-0.5" />
                            <span className="text-rnb-navy">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Note */}
      <section className="py-16 bg-rnb-light">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-rnb-navy mb-4">
            Affordable and Transparent Consulting for Pharma & Biotech Startups
          </h2>
          <p className="text-rnb-gray mb-8">
            At RNB Consulting Group we know that growing teams need expert support without breaking 
            the budget. Our services are priced clearly and transparently so you always know what 
            you are investing in. We offer flexible and scalable solutions that deliver real results, 
            helping your team streamline operations, stay compliant, and optimize workflows while 
            keeping costs manageable for startups and small businesses.
          </p>
          <Link to="/contact">
            <Button size="lg" className="btn-hover-lift">
              Get Your Customized Quote
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Mid-page CTA */}
      <MidPageCTA variant={1} />

      {/* Why Trust Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Why Startups & Small Teams Trust RNBCG
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {whyTrustUs.map((item, index) => (
              <Card key={item.title || item.name || item.label || index} className="border-0 shadow-sm card-hover">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-rnb-navy/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Building2 className="w-8 h-8 text-rnb-navy" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-rnb-navy mb-3">
                    {item.title}
                  </h3>
                  <p className="text-rnb-gray text-sm leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mb-6">
                Ready to Streamline Your Pharma or Biotech Team?
              </h2>
              <p className="text-rnb-gray text-lg mb-8">
                Let RNBCG turn operational chaos into clarity. Whether it's operations, HR, or 
                data management, we help small teams run smarter, faster, and more efficiently.
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-rnb-blue/10 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-rnb-blue" />
                  </div>
                  <div>
                    <p className="font-medium text-rnb-navy">RNB Consulting Group</p>
                    <p className="text-sm text-rnb-gray">650 Ponce De Leon Avenue Northeast, Atlanta, GA</p>
                  </div>
                </div>
                <p className="text-rnb-gray text-sm">
                  <strong>Hours:</strong> Mon-Fri 9:00 AM - 5:00 PM EST
                </p>
              </div>
            </div>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={4}
                    />
                  </div>
                  <Button type="submit" className="w-full btn-hover-lift" disabled={loading}>
                    {loading ? "Sending..." : "Send"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LifeSciences;
